/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { request } from '@mairistem/tools';

/**
 * Charge un référentiel exposé au format `{ identifiant, libelle, ... }[]`
 * (pattern `SousTypeActeResource`/`TypeReconnaissanceResource`/
 * `AutoriteReconnaissanceResource`) et en dérive les `options` d'un
 * `Form.Dropdown` (`identifiant` en `value`, `libelle` en `text`).
 *
 * `cleValeur` désigne le champ porté en `value` (défaut `identifiant`,
 * comportement historique). Certains référentiels sont référencés par leur CODE
 * et non par leur identifiant : l'état matrimonial d'un acte de décès stocke
 * `em_codj` dans `ad_df_ematc`, jamais `em_auto`.
 *
 * Expose aussi la liste brute (`items`) : indispensable pour retrouver l'item
 * sélectionné et en dériver un code métier (ex. `codeSousTypeActe`) que le
 * dropdown, qui ne connaît que l'`identifiant`, ne peut pas fournir seul.
 *
 * Mutualise le `useEffect`/`request.get` auparavant dupliqué dans
 * `InformationsActe` (et `InformationsEvenementReconnaissance`).
 */
/**
 * Items d'un référentiel → options d'un `Form.Dropdown`.
 *
 * Extraite du hook pour être testable sans réseau, comme
 * `choisirRegistreParDefaut` : c'est la RÈGLE qui doit être verrouillée, pas le
 * `request.get`.
 *
 * ⚠️ Dédoublonnage par valeur : `ec_etamat` filtré sur `type=D_DEFUNT` a DEUX
 * lignes de `codeJustice = 'P'` (Pacsé(e), `em_auto` 14 et 18), qui
 * produiraient deux options indiscernables et deux clés React identiques.
 */
export const optionsDepuisReferentiel = (
  items: Record<string, any>[],
  cleValeur = 'identifiant',
) =>
  _.map(_.uniqBy(items, cleValeur), (item) => ({
    key: item[cleValeur],
    value: item[cleValeur],
    text: item.libelle,
  }));

const useOptionsReferentiel = (
  url: string | null | undefined,
  cleValeur = 'identifiant',
) => {
  const [items, setItems] = React.useState<Record<string, any>[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (_.isEmpty(url)) {
      setItems([]);
      return;
    }

    setLoading(true);
    request
      .get(url as string)
      .then(({ data }: { data: Record<string, any>[] }) => {
        setItems(data ?? []);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [url]);

  const options = React.useMemo(
    () => optionsDepuisReferentiel(items, cleValeur),
    [items, cleValeur],
  );

  return { options, items, loading };
};

export default useOptionsReferentiel;
